import http from 'isomorphic-git/http/web';
import git from 'isomorphic-git';
import { type ReadCommitResult } from 'isomorphic-git';
import * as ZenCore from '@zenfs/core';
import { unzipSync } from 'fflate';
import { join as pathJoin, dirname as pathDirname } from 'path';
import ignore from 'ignore';
import { isDir } from './filesystem';
import { toString } from './utility';

const isZipUrl = (u: string) => u.toLowerCase().endsWith('.zip');
const projectDir = (name: string) => `${window.miDir}/${name}`;

// DO NOT use this function for non zenfs based fs since they won't have
// mkdir recursive support.
const zenfsWriteZipEntry = async (root: string, relPath: string, data: Uint8Array | null) => {
  // Any entry name that ends with "/" is a directory in a ZIP archive.
  const isDirEntry = relPath.endsWith('/');
  const fullPath = pathJoin(root, isDirEntry ? relPath.slice(0, -1) : relPath);
  if (isDirEntry) {
    // If its just a dir create it recursively and just return
    await window.pfs.mkdir(fullPath, { recursive: true });
    return;
  }

  // File case: create parent dir and then write the file
  const dir = pathDirname(fullPath);
  await window.pfs.mkdir(dir, { recursive: true });
  await window.pfs.writeFile(fullPath, data as Uint8Array);
};

const _syncZipSource = async (projectName: string, projectUrl: string): Promise<void> => {
  // We only support one project at a time so this is safe
  window.fs = ZenCore.fs;
  window.pfs = ZenCore.fs.promises;

  // Wipe out the fs just in case
  await ZenCore.configureSingle({ backend: ZenCore.InMemory });

  const targetDir = projectDir(projectName);
  const res = await fetch(projectUrl);
  if (!res.ok) {
    throw new Error(`Failed to fetch zip: ${res.status} ${res.statusText}`);
  }

  const zipU8 = new Uint8Array(await res.arrayBuffer());
  const entries = unzipSync(zipU8);

  // NOTE: doing the copies in parallel doesn't work properly so avoid it
  for (const [rawPath, fileData] of Object.entries(entries)) {
    // Remove front forward slash, then replace backslashes with forward slashes
    const relPath = rawPath.replace(/^\/+/, '').replace(/\\/g, '/');
    await zenfsWriteZipEntry(targetDir, relPath, fileData as Uint8Array | null);
  }

  // NOTE:
  // I don't think this is actually necessary for the zip based approach since
  // I think this was only needed for when we used git.pull() which may generate
  // a merge commit (which in turn needs an author).
  await _setGitConfig(projectName);
};

const _setGitConfig = async (projectName: string) => {
  const pfs = window.pfs;
  const dir = projectDir(projectName);

  await git.setConfig({
    fs: pfs,
    dir,
    path: 'user.name',
    value: 'makeinfinite_client',
  });
  await git.setConfig({
    fs: pfs,
    dir,
    path: 'user.email',
    value: 'makeinfinite_client@spaceandtime.io',
  });
};

const checkoutPublished = async (projectName: string) =>
  git
    .resolveRef({
      fs: window.pfs,
      dir: projectDir(projectName),
      ref: 'refs/tags/published',
    })
    .then((oid) =>
      git.checkout({
        fs: window.pfs,
        dir: projectDir(projectName),
        ref: oid,
      })
    );

// TODO: We shouldn't need this anymore once the FE is updated to use the
// backend API for undo/redo
class ResetFileCommitMessage {
  constructor(
    public readonly targetRef: string,
    public readonly sourceRef: string,
    public readonly opType: string
  ) {}
}

const parseResetFileCommit = (commit: ReadCommitResult): ResetFileCommitMessage | null => {
  const messageLines = commit.commit.message.trim().split('\n');
  const gitRefLine = messageLines.find((line) => line.startsWith('Git Ref: '));
  const extraMetadataLine = messageLines.find((line) => line.startsWith('Extra Metadata: '));
  if (!gitRefLine || !extraMetadataLine) return null;

  const gitRef = gitRefLine.split('Git Ref: ')[1]?.trim();
  const extraMeta = JSON.parse(extraMetadataLine.split('Extra Metadata: ')[1]?.trim() || '{}');
  if (!gitRef || !extraMeta) return null;

  return new ResetFileCommitMessage(gitRef, extraMeta['sourceRef'], extraMeta['opType']);
};

const getHeadCommit = async (projectName: string) => {
  const pfs = window.pfs;
  const targetDir = `${window.miDir}/${projectName}`;

  const oid = await git.resolveRef({
    fs: pfs,
    dir: targetDir,
    ref: 'HEAD',
  });
  return oid;
};

// TODO: We shouldn't need this anymore once the FE is updated to use the
// backend API for undo/redo
const getHEADCommitForFile = async (projectName: string, filePath: string): Promise<string> => {
  const pfs = window.pfs;
  const targetDir = `${window.miDir}/${projectName}`;
  const logOptions = {
    fs: pfs,
    dir: targetDir,
    ref: 'HEAD',
    depth: 1,
    filepath: filePath,
  };
  const commit = (await git.log(logOptions))[0];
  if (!commit) {
    throw new Error(`No commit found`);
  }
  return commit.oid;
};

// TODO: We shouldn't need this anymore once the FE is updated to use the
// backend API for undo/redo
const _resolveToConcreteCommit = async (
  projectName: string,
  filePath: string,
  ref: string,
  recurseOnOpType: string,
  seenCommits: Set<string> = new Set(),
  maxDepth = 1000
): Promise<ReadCommitResult> => {
  if (maxDepth <= 0) {
    throw new Error(`Maximum recursion depth reached while resolving commit: ${ref}`);
  }

  const pfs = window.pfs;
  const targetDir = `${window.miDir}/${projectName}`;

  const logOptions = {
    fs: pfs,
    dir: targetDir,
    ref: ref,
    depth: 1,
    filepath: filePath,
  };
  const commit = (await git.log(logOptions))[0];
  if (!commit) {
    throw new Error(`No commit found`);
  }

  if (seenCommits.has(commit.oid)) {
    throw new Error(`Circular reference detected: ${commit.oid} has already been seen`);
  }
  seenCommits.add(commit.oid);

  const resetFileCommitMessage = parseResetFileCommit(commit);
  // If this is a 'Reset file' commit we need to "walk" the ref to find the
  // actual base commit. This is recursive because the base commit that it
  // points to could be yet another 'Reset file' commit.
  if (resetFileCommitMessage && resetFileCommitMessage.opType == recurseOnOpType) {
    return _resolveToConcreteCommit(
      projectName,
      filePath,
      resetFileCommitMessage.targetRef,
      recurseOnOpType,
      seenCommits,
      maxDepth - 1
    );
  } else if (commit.commit.parent.length > 1) {
    // A commit with more than one parent is a merge commit, which is not a
    // concrete commit. So make a recursive call to continue resolving it.
    const baseCommitOid = commit.commit.parent[0];
    if (!baseCommitOid) {
      throw new Error(`No parent found`);
    }

    return _resolveToConcreteCommit(
      projectName,
      filePath,
      baseCommitOid,
      recurseOnOpType,
      seenCommits,
      maxDepth - 1
    );
  }

  // Otherwise we're at a concrete commit
  return commit;
};

// TODO: We shouldn't need this anymore once the FE is updated to use the
// backend API for undo/redo
const getUndoTargetCommitForFile = async (
  projectName: string,
  filePath: string
): Promise<[string, string]> => {
  const pfs = window.pfs;
  const targetDir = `${window.miDir}/${projectName}`;
  const headCommit = await getHEADCommitForFile(projectName, filePath);
  const concreteBaseCommit = await _resolveToConcreteCommit(
    projectName,
    filePath,
    headCommit,
    'UNDO'
  );

  // At this point the concreteBaseCommit can only either be a
  // ResetFileCommitMessage "REDO" or an actual normal commit
  const resetFileCommitMessage = parseResetFileCommit(concreteBaseCommit);
  if (resetFileCommitMessage) {
    // In this case we need to use the sourceRef commit
    return [headCommit, resetFileCommitMessage.sourceRef];
  } else {
    // This is the normal commit case, where we just need to get the previous
    // commit
    let concreteBaseRef = concreteBaseCommit.oid;
    const logOptions = {
      fs: pfs,
      dir: targetDir,
      ref: concreteBaseRef,
      depth: 2,
      filepath: filePath,
    };

    const gitLog = await git.log(logOptions);
    if (!gitLog[1]) {
      throw new Error('No previous commit found');
    }
    const previousCommitRef = gitLog[1].oid;
    return [headCommit, previousCommitRef];
  }
};

// TODO: We shouldn't need this anymore once the FE is updated to use the
// backend API for undo/redo
const getRedoTargetCommitForFile = async (
  projectName: string,
  filePath: string
): Promise<[string, string]> => {
  const headCommit = await getHEADCommitForFile(projectName, filePath);
  const concreteBaseCommit = await _resolveToConcreteCommit(
    projectName,
    filePath,
    headCommit,
    'REDO'
  );
  // Redo is only supported for "UNDO" base commits
  const resetFileCommitMessage = parseResetFileCommit(concreteBaseCommit);
  // Note that we don't need to check the opType here since the
  // resolveToConcreteCommit() call above would have only returned either
  // a normal commit or an UNDO commit.
  if (!resetFileCommitMessage) {
    throw new Error('No undos to redo');
  }
  return [headCommit, resetFileCommitMessage.sourceRef];
};

const _syncGitSource = async (projectName: string, repoURL: string) => {
  try {
    const exists = await isDir(projectDir(projectName));
    if (!exists) {
      await git.clone({
        fs: window.pfs,
        http,
        dir: projectDir(projectName),
        url: repoURL,
        ref: 'main',
      });
      // NOTE: I don't think we need _setGitConfig anymore now that we are using
      // fastForward().
      await _setGitConfig(projectName);
    } else {
      // Fetch then fast-forward
      await git.fetch({
        fs: window.pfs,
        http,
        dir: projectDir(projectName),
        url: repoURL,
        ref: 'main',
        tags: true,
        pruneTags: true,
      });

      // NOTE: fastForward has a slight redundancy here with fetch since
      // it will do a pull underneath which will also do a fetch, but the
      // problem with the fetch call that it makes is that it doesn't pass
      // in: `tags: true` or allow us to pass it in either.
      // This won't matter once we switch completely to using the zip approach.
      await git.fastForward({
        fs: window.pfs,
        http,
        dir: projectDir(projectName),
        url: repoURL,
        ref: 'main',
      });
    }
  } catch (err) {
    // If there's an issue with cloning or pulling wipe out the fs
    await window.fs.init('fs', { wipe: true });
    // rethrow
    throw err;
  }
};

// Map to track ongoing sync operations to prevent parallel duplicate work
const syncOperations = new Map<string, Promise<void>>();

// Internal implementation that does the actual sync work
const _syncAndRetryImpl = async (projectName: string, projectRepository: string): Promise<void> => {
  const maxRetries = 5;
  let attempts = 0;

  while (attempts <= maxRetries) {
    try {
      if (isZipUrl(projectRepository)) {
        await _syncZipSource(projectName, projectRepository);
      } else {
        await _syncGitSource(projectName, projectRepository);
      }
      return; /* success – bail out */
    } catch (err) {
      // Clear out the fs entirely on an error
      console.error('syncAndRetry error', err);
      attempts += 1;
      if (attempts > maxRetries) throw err;
    }
  }
};

// the single entry-point used everywhere else for syncing
const syncAndRetry = async (projectName: string, projectRepository: string): Promise<void> => {
  // Create a unique key for this sync operation
  const operationKey = `${projectName}:${projectRepository}`;

  // Check if there's already an ongoing operation for this key
  const existingOperation = syncOperations.get(operationKey);
  if (existingOperation) {
    // Return the existing promise to avoid duplicate work
    return existingOperation;
  }

  // Start a new sync operation
  const syncPromise = _syncAndRetryImpl(projectName, projectRepository).finally(() => {
    // Clean up the operation from the map when it completes (success or failure)
    syncOperations.delete(operationKey);
  });

  // Store the promise in the map
  syncOperations.set(operationKey, syncPromise);

  return syncPromise;
};

type GetTagsOptions = {
  fetch?: boolean;
};

const getTags = async (
  projectName: string,
  projectUrl: string,
  {
    // true by default to maintain backwards compatibility with users of
    // this function
    fetch = true,
  }: GetTagsOptions = {}
) => {
  // TODO: We should not call syncAndRetry here, the caller should do so if
  // they need the repo to be updated.
  if (fetch) {
    await syncAndRetry(projectName, projectUrl);
  }

  const pfs = window.pfs;
  const dir = projectDir(projectName);

  let tags = await git.listTags({ fs: pfs, dir });

  return Promise.all(
    tags.map(async (tag) => {
      const tagOid = await git.resolveRef({ fs: pfs, dir, ref: `refs/tags/${tag}` });
      let commitOid: string;
      try {
        commitOid = (await git.readTag({ fs: pfs, dir, oid: tagOid })).tag.object;
      } catch {
        commitOid = tagOid; // lightweight
      }

      const { commit } = await git.readCommit({ fs: pfs, dir, oid: tagOid });
      return {
        tag,
        targetCommitTimestamp: commit.committer.timestamp,
        targetCommit: commitOid,
        ref: `refs/tags/${tag}`,
      };
    })
  );
};

const listFilesUsingGit = async (fsImpl: any, dir: string) =>
  git.listFiles({ fs: fsImpl, dir }).catch(() => []);

const applySxtIgnore = async (files: string[], pfs: any, dir: string) => {
  const ig = ignore();
  const raw = await pfs.readFile(`${dir}/.sxtignore`, 'utf8');
  ig.add(
    toString(raw)
      .split('\n')
      .map((l) => l.trim())
      .filter((l) => l)
  );
  return ig.filter(files);
};

const listAllFiles = async (projectName: string, repoURL: string) => {
  // TODO: We should not call syncAndRetry here, the caller should do so if
  // they need the repo to be updated.
  await syncAndRetry(projectName, repoURL);
  const fsImpl = window.pfs;
  const dir = projectDir(projectName);
  return listFilesUsingGit(fsImpl, dir);
};

interface Commit {
  hash: string;
  timestamp: number;
  message: string;
  isRelease: boolean;
}

const getCommits = async (
  projectName: string,
  projectUrl: string,
  commitsCount?: number
): Promise<Commit[]> => {
  try {
    const pfs = window.pfs;
    const targetDir = `${window.miDir}/${projectName}`;

    // TODO: Do not sync in getCommits, the caller should sync before calling
    // getCommits if it really needs the latest changes.

    // Pull the latest changes to ensure the full commit history is up to date.
    await syncAndRetry(projectName, projectUrl);

    const commits = await git.log({
      fs: pfs,
      dir: targetDir,
      depth: commitsCount,
      ref: 'main',
    });

    // Map the commits to our Commit type.
    const commitList: Commit[] = commits.map((commitObj) => {
      const { oid, commit } = commitObj;
      const message = commit.message;
      // TODO: I don't see where we ever put the "release" keyword inside of
      // a commit message.

      // Mark as a release if the commit message includes "release" (case-insensitive).
      const isRelease = message.toLowerCase().includes('release');
      return { hash: oid, timestamp: commit.committer.timestamp, message, isRelease };
    });

    return commitList;
  } catch (error) {
    console.error('Error in gitCommits:', error);
    throw error;
  }
};

// TODO: We should be able to get rid of this useless wrapper at some point
const checkoutCommit = async (projectName: string, commitHash: string) =>
  git.checkout({
    fs: window.pfs,
    dir: projectDir(projectName),
    ref: commitHash,
    force: true,
  });

export {
  getHeadCommit,
  getUndoTargetCommitForFile,
  getRedoTargetCommitForFile,
  syncAndRetry,
  getTags,
  checkoutPublished,
  applySxtIgnore,
  listAllFiles,
  getCommits,
  checkoutCommit,
};
